import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { trackError } from '@local/metrics';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/GridLegacy';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { skipToken } from '@reduxjs/toolkit/query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { decodeToken } from 'react-jwt';
import { Navigate, useNavigate } from 'react-router';
import { useLoadBentleyLicenseOrgsQuery } from '../../../apiClients/entitlementsClient/requests/BentleyEntitlementsAPI';
import { EVO_APPS, EVO_NEXT_SERVICES, LOGIN_ERROR_ROUTE, LOGIN_ROUTE } from '../../../constants';
import { getCombinedToken as getAccessToken } from '../../../store/sessionStorageHelpers/accessTokenHelper/accessTokenHelper';
import { getDeeplink, getHubFromDeeplink, getOrgFromDeeplink, } from '../../../store/sessionStorageHelpers/deeplinkHelper/deeplinkHelper';
import { getCurrentEvoInstance, getLastUsedHub, setCurrentEvoInstance, setLastUsedHub, } from '../../../store/sessionStorageHelpers/entitlementsHelper/entitlementsHelper';
import { Background } from '../../background/Background';
import { LoginContext } from '../../loginHandler/LoginContext';
import { OrgHubSelectMenu } from '../OrgHubSelectMenu/OrgHubSelectMenu';
import { useStyles } from '../sharedSelector.styles';
import { getEvoInstanceForOrgIdWithOptionalHubCode } from '../utils';
const sortByDisplayName = (a, b) => a.display_name.toLowerCase() > b.display_name.toLowerCase() ? 1 : -1;
function evoOptionsGroups(data, evoMap) {
    const response = [];
    if (data) {
        [...data.organizations].sort(sortByDisplayName).forEach((org) => {
            const hubs = [];
            for (const entry of evoMap.entries()) {
                if (entry[1].org.id === org.id) {
                    hubs.push(entry[1].hub);
                }
            }
            if (hubs.length > 0) {
                response.push({
                    label: org.display_name,
                    optionIds: hubs.sort(sortByDisplayName).map((hub) => `${org.id}-${hub.code}`),
                });
            }
        });
    }
    return response;
}
function generateEvoMap(data, service) {
    const map = new Map();
    if (data) {
        const hubs = new Map();
        for (const hub of data.hubs) {
            hubs.set(hub.code, hub);
        }
        const orgs = new Map();
        for (const org of data.organizations) {
            orgs.set(org.id, org);
        }
        const services = Array.isArray(service) ? service : [service];
        for (const sa of data.service_access) {
            const hub = hubs.get(sa.hub_code);
            const org = orgs.get(sa.org_id);
            if (services.some((s) => sa.services.includes(s)) && hub && org) {
                map.set(`${sa.org_id}-${sa.hub_code}`, { org, hub });
            }
        }
    }
    return map;
}
function labelComponent(labelHeader, label) {
    return () => (_jsxs(Stack, { direction: "column", alignItems: "left", width: "320px", children: [_jsx(Typography, { variant: "body2", children: labelHeader }), _jsx(Typography, { variant: "body2", sx: { fontSize: '12px !important' }, children: label })] }));
}
function instances(data) {
    const items = [];
    for (const entry of data.entries()) {
        items.push({
            key: entry[0],
            label: labelComponent(entry[1].org.display_name, `${entry[1].hub.display_name} (${entry[1].hub.code})`),
        });
    }
    return items.sort();
}
function getDecodedIDToken(accessToken) {
    if (accessToken && accessToken.id_token) {
        return decodeToken(accessToken.id_token);
    }
    return null;
}
export function BentleyOrgHubSelector({ homePagePath = '/', evoNextEnabled = false, onSuccess, }) {
    var _a, _b;
    const { service } = useContext(LoginContext);
    const { classes } = useStyles();
    const navigate = useNavigate();
    const accessToken = getAccessToken();
    const { evouiCombineDevIntgInstanc: combineResponses } = useFlags();
    const serviceForQuery = service === 'evo-next' ? EVO_NEXT_SERVICES : service;
    const evoData = useLoadBentleyLicenseOrgsQuery((accessToken === null || accessToken === void 0 ? void 0 : accessToken.access_token) && service !== 'none'
        ? Object.assign({ service: serviceForQuery }, (combineResponses && EVO_APPS.includes(service) && { combineResponses })) : skipToken);
    const evoMap = useMemo(() => generateEvoMap(evoData.data, serviceForQuery), [evoData.data, serviceForQuery]);
    const optionGroups = evoOptionsGroups(evoData.data, evoMap);
    const selected = evoData.data && optionGroups.length ? optionGroups[0].optionIds[0] : '';
    const [selectedInstance, setSelectedInstance] = useState('');
    const [fetchToken, setFetchToken] = useState(false);
    useEffect(() => {
        if (evoMap.size === 1 && !fetchToken) {
            const instance = [...evoMap.entries()][0][1];
            continueWithInstance(instance);
        }
    }, [evoMap]);
    const decodedToken = getDecodedIDToken(accessToken);
    const userName = decodedToken ? `${decodedToken === null || decodedToken === void 0 ? void 0 : decodedToken.given_name} ${decodedToken === null || decodedToken === void 0 ? void 0 : decodedToken.family_name}` : '';
    const expiredOrLackingToken = !accessToken ||
        (accessToken && accessToken.expires_at <= Date.now() && !(accessToken === null || accessToken === void 0 ? void 0 : accessToken.refresh_token));
    useEffect(() => {
        const { data, error, isLoading } = evoData;
        if (error) {
            trackError('Error loading instances', `Error loading instances for ${service} in <BentleyOrgHubSelector>`, {
                evo: error,
            });
            navigate(`${LOGIN_ERROR_ROUTE}?error=entitlements_failure&bentley_token=true`);
            return;
        }
        if (isLoading)
            return;
        if ((data === null || data === void 0 ? void 0 : data.service_access.length) === 0) {
            setCurrentEvoInstance({ hasNoLicense: true });
            if (onSuccess) {
                onSuccess(getCurrentEvoInstance());
            }
            else {
                navigate(homePagePath);
            }
            return;
        }
        if (!data) {
            trackError(`Invalid Bentley license scope provided for ${service}`, 'No valid org/hub for service');
            navigate(`${LOGIN_ERROR_ROUTE}?error=entitlements_failure&bentley_token=true`);
        }
    }, [evoData, evoNextEnabled]);
    const continueWithInstance = useCallback((instance) => {
        var _a, _b, _c;
        if (!instance) {
            return;
        }
        setLastUsedHub((_a = instance.hub) === null || _a === void 0 ? void 0 : _a.code);
        setCurrentEvoInstance({ org: instance.org, hub: instance.hub });
        setSelectedInstance(`${(_b = instance === null || instance === void 0 ? void 0 : instance.org) === null || _b === void 0 ? void 0 : _b.id}-${(_c = instance === null || instance === void 0 ? void 0 : instance.hub) === null || _c === void 0 ? void 0 : _c.code}`);
        setFetchToken(true);
    }, []);
    if (expiredOrLackingToken) {
        return _jsx(Navigate, { to: LOGIN_ROUTE });
    }
    const deeplink = getDeeplink(homePagePath);
    const orgFromDeeplink = getOrgFromDeeplink(deeplink, (_a = evoData === null || evoData === void 0 ? void 0 : evoData.data) === null || _a === void 0 ? void 0 : _a.organizations);
    if (orgFromDeeplink) {
        const hubFromDeeplink = getHubFromDeeplink(deeplink, (_b = evoData === null || evoData === void 0 ? void 0 : evoData.data) === null || _b === void 0 ? void 0 : _b.hubs);
        const evoInstance = getEvoInstanceForOrgIdWithOptionalHubCode(orgFromDeeplink.id, evoData.data, hubFromDeeplink === null || hubFromDeeplink === void 0 ? void 0 : hubFromDeeplink.code);
        if (!evoInstance) {
            return _jsx(Navigate, { to: LOGIN_ROUTE });
        }
        setCurrentEvoInstance(evoInstance);
        return _jsx(Navigate, { to: deeplink });
    }
    if (fetchToken && accessToken) {
        const instance = evoMap.get(selectedInstance);
        if (onSuccess && instance) {
            onSuccess(instance);
            return null;
        }
        if (evoNextEnabled) {
            return _jsx(Navigate, { to: homePagePath });
        }
        else if (instance === null || instance === void 0 ? void 0 : instance.org.id) {
            return (_jsx(Navigate, { to: `/${instance.org.id}/${homePagePath.startsWith('/') ? homePagePath.substring(1) : homePagePath}` }));
        }
        else {
            trackError('Error loading instance', `Selection ${selectedInstance} is missing in <BentleyOrgHubSelector>`, { evoMapKeys: Array.from(evoMap.keys()).join(', ') });
            return (_jsx(Navigate, { to: `/${homePagePath.startsWith('/') ? homePagePath.substring(1) : homePagePath}` }));
        }
    }
    const expired = accessToken && accessToken.expires_at <= Date.now();
    const invalidToken = accessToken === null || Object.keys(accessToken).length === 0;
    if (expired || invalidToken) {
        return _jsx(Navigate, { to: LOGIN_ROUTE });
    }
    const lastUsedHubCode = !(evoData === null || evoData === void 0 ? void 0 : evoData.isLoading) ? getLastUsedHub() : undefined;
    if (lastUsedHubCode) {
        continueWithInstance(lastUsedHubCode);
    }
    return (evoData === null || evoData === void 0 ? void 0 : evoData.isLoading) ? (_jsx(Background, { children: _jsx(Grid, { item: true, style: {
                position: 'relative',
                width: '100%',
                height: '100px',
            }, alignContent: "center", justifyContent: "center", display: "grid", children: _jsx(CircularProgress, { "automation-id": "spinner-loading" }) }) })) : (_jsx(Background, { children: _jsxs(Grid, { item: true, xs: 12, className: classes.formContainer, children: [_jsxs("div", { className: classes.label, children: [_jsxs(Typography, { children: ["Name: ", userName || null] }), _jsx(Grid, { item: true, xs: true, className: classes.selectMenu, children: _jsx(OrgHubSelectMenu, { options: instances(evoMap), onSelected: (key) => setSelectedInstance(key), defaultValue: selected, width: 360 }) })] }), _jsx(Button, { color: "primary", variant: "contained", size: "large", className: classes.button, onClick: () => continueWithInstance(evoMap.get(selectedInstance)), disabled: !selectedInstance, id: "continue-btn", children: "Continue" })] }) }));
}
//# sourceMappingURL=BentleyOrgHubSelector.js.map